import 'core-js';
import 'whatwg-fetch';
import React, { useEffect, useState } from 'react';
import { createTheme, ThemeProvider } from '@mui/material';
import { SToastViewport } from '@chargebee/sting-react';
import { type APIError } from '@bbkAdminRedux/rtkq/rtkq-error-logger';
import { detectIE } from '@bbkAdminUtils/detect-ie';
import { renderInlineToDOM } from '@bbkAdminUtils/render_client_side_funnel';
import { FunnelContext } from '@bbkFunnel/components/FunnelContext';
import Funnel from '@bbkFunnel/pages/Funnel';
import {
  type FailedExperienceResponse,
  type RuntimeExperienceResponse,
} from '@bbkFunnelComponents/funnel-types';
import {
  getPageLoadModalCardTheme,
  resolveFunnelTheme,
} from '@bbkFunnelUtils/funnel-utils';
import { PricingPageShimmerLoader } from '@bbkPricingPage/components/loader/pricing-page-shimmer-loader';
import { defaultMUiTheme } from '@bbkShared/material-ui-theme';
import { matchPath } from '@bbkShared/react-router';

const FunnelLoader: React.FC = () => {
  const [response, setResponse] = useState<RuntimeExperienceResponse>();
  const [isPricingPage, setIsPricingPage] = useState<boolean>(false);
  const [pricingPageSessionId, setPricingPageSessionId] = useState<
    string | undefined
  >();
  const [error, setError] = useState<string>();
  const [pricingPageKey, setPricingPageKey] = useState<string | undefined>();
  const [legacySiteId, setLegacySiteId] = useState<string | undefined>();
  const [legacyPricingPageId, setLegacyPricingPageId] = useState<
    string | undefined
  >();
  const [defaultCurrency, setDefaultCurrency] = useState<string | undefined>();

  const theme = createTheme(defaultMUiTheme);

  // Extract host from URL params at component level
  const params = new URLSearchParams(window.location.search);
  const host = params.get('host') || undefined;
  const variantId = params.get('variant-id') || undefined;

  const loadExperience = (): void => {
    const pathName = window.location.pathname;
    const splitPath = pathName.split('/');
    const sessionKey = splitPath[2];
    let path: string | undefined;

    const isGeoPath = window.location.pathname.startsWith('/geo/');

    // Handle pricing page session route: /pricing/session/{sessionId} or /geo/pricing/session/{sessionId}
    const pricingPageSession =
      matchPath<{ sessionId: string }>(
        window.location.pathname,
        '/pricing/session/:sessionId'
      ) ||
      matchPath<{ sessionId: string }>(
        window.location.pathname,
        '/geo/pricing/session/:sessionId'
      );
    const sessionId = pricingPageSession?.params.sessionId;

    // Handle pricing page route: /pricing/{pricingPageKey} or /geo/pricing/{pricingPageKey}
    const pricingPage =
      matchPath<{ pricingPageKey: string }>(
        window.location.pathname,
        '/pricing/:pricingPageKey'
      ) ||
      matchPath<{ pricingPageKey: string }>(
        window.location.pathname,
        '/geo/pricing/:pricingPageKey'
      );
    const extractedPricingPageKey = pricingPage?.params.pricingPageKey;

    // Atomic Pricing backward-compat: /sites/{siteId}/pricing/{atomicPricingPageId}
    const atomicPricingPage =
      matchPath<{ siteId: string; atomicPricingPageId: string }>(
        window.location.pathname,
        '/sites/:siteId/pricing/:atomicPricingPageId'
      ) ||
      matchPath<{ siteId: string; atomicPricingPageId: string }>(
        window.location.pathname,
        '/geo/sites/:siteId/pricing/:atomicPricingPageId'
      );

    setPricingPageKey(extractedPricingPageKey);
    setLegacySiteId(undefined);
    setLegacyPricingPageId(undefined);

    const variantIdParam = variantId ? `?variant_id=${variantId}` : '';

    if (sessionId) {
      path = isGeoPath
        ? `/geo/pricing/session/${sessionId}/render${variantIdParam}`
        : `/pricing/session/${sessionId}/render${variantIdParam}`;
      setIsPricingPage(true);
      setPricingPageSessionId(sessionId);
    } else if (extractedPricingPageKey) {
      path = isGeoPath
        ? `/geo/pricing/${extractedPricingPageKey}/render${variantIdParam}`
        : `/pricing/${extractedPricingPageKey}/render${variantIdParam}`;
      setIsPricingPage(true);
      setPricingPageSessionId(undefined);
    } else if (atomicPricingPage) {
      const { siteId, atomicPricingPageId } = atomicPricingPage.params;
      setLegacySiteId(siteId);
      setLegacyPricingPageId(atomicPricingPageId);
      path = isGeoPath
        ? `/geo/sites/${siteId}/pricing/${atomicPricingPageId}/render${variantIdParam}`
        : `/sites/${siteId}/pricing/${atomicPricingPageId}/render${variantIdParam}`;
      setIsPricingPage(true);
      setPricingPageSessionId(undefined);
    } else if (sessionKey) {
      path = `/api/v1/session/${sessionKey}?load_experience`;
    } else if (/preview-experience/.test(pathName)) {
      path = `/api/v1/preview-experience${window.location.search}`;
    }
    if (path) {
      fetch(path)
        .then(async (resp) => {
          const json = await resp.json();
          if (!resp.ok) {
            throw new Error((json as FailedExperienceResponse).message);
          }
          const data = json as RuntimeExperienceResponse;
          window.bbkClientConfig = data?.config;
          window.bbkFeatureFlags = data?.globalFeatureFlags;
          window.bbkAuthenticityToken = data?.authenticity;

          const geoCurrency = resp.headers.get('X-Geo-Resolved-Currency');
          setDefaultCurrency(geoCurrency || undefined);

          setPricingPageKey(
            data.pricing_page_key ?? extractedPricingPageKey ?? undefined
          );

          setResponse(data);
        })
        .catch((e: APIError) => {
          setError(
            e.message ??
              'An error occurred while loading this page. Please try again later or contact support.'
          );
        });
    }
  };
  useEffect(() => {
    if (response) {
      const fav = document.getElementById('favicon') as HTMLLinkElement | null;
      if (fav) {
        const title =
          response?.experience?.company?.name ??
          (isPricingPage ? 'Pricing' : 'Cancel');
        const env =
          response.config.environment === 'production'
            ? ''
            : `[${response.config.environment}] `;
        document.title = `${env}${title}`;
        fav.href = response.favicon || '/assets/images/favicon.png';
      }
    }
  }, [response, isPricingPage]);

  useEffect(() => {
    setInterval(loadExperience, 1000 * 60 * 60 * 24); // refresh every 24 hours to keep integrity token, config, and feature flags fresh
    loadExperience();
  }, []);

  // Add full-height class for pricing pages
  useEffect(() => {
    if (!isPricingPage) return;
    const htmlElement = document.documentElement;
    if (htmlElement) {
      htmlElement.classList.add('pricing-page-full-height');
    }
    return () => {
      if (htmlElement) {
        htmlElement.classList.remove('pricing-page-full-height');
      }
    };
  }, [isPricingPage]);

  const centerCss = {
    height: '100vh',
    width: '100vw',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0,0,0,0.1)',
    color: 'rgba(0,0,0,0.6)',
  };
  if (error) {
    return <div css={{ ...centerCss }}>{error}</div>;
  }
  if (!response) {
    return isPricingPage ? (
      <PricingPageShimmerLoader isFullScreen={true} />
    ) : (
      <div css={centerCss}>Loading...</div>
    );
  }

  const experience = response.experience;
  const emotionTheme = resolveFunnelTheme({
    layout: experience?.layout ?? { style_overlay: {} },
    locale: response.locale,
    cardLevelTheme: experience
      ? getPageLoadModalCardTheme(experience.layout, experience.config)
      : undefined,
  });

  return (
    <FunnelContext.Provider
      value={{
        isPricingPage,
        pricingPageProps: {
          pricingPageEVId: response?.pricing_page_ev_id ?? 0,
          pricingPageId: response?.pricing_page_id ?? '',
          appId: response?.app_id ?? '',
          siteId: response?.site_id ?? '',
          legacySiteId,
          legacyPricingPageId,
          host,
          pricingPageKey,
          sessionId: pricingPageSessionId,
          discounts: response?.discounts,
          subscription: response?.subscription,
          defaultCurrency,
          priceVariantId: variantId,
        },
      }}
    >
      <ThemeProvider theme={theme}>
        <SToastViewport position="bottom-right" />
        <Funnel {...experience} emotionTheme={emotionTheme} />
      </ThemeProvider>
    </FunnelContext.Provider>
  );
};

if (detectIE()) {
  document.querySelector('html')?.classList.add('internetExplorer');
}
renderInlineToDOM(FunnelLoader, {});
